﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import {
    type IntlContext,
    useIntl,
} from "~base/Internationalization/IntlContext";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import useStyles from "./Styles";
import {
    type SelfServiceLoginViewAction,
    initialState,
    reducer,
} from "./Store";
import {
    SelfServiceLoginRegistrationPage,
    type SelfServiceLoginViewState,
} from "./Types";
import { type ApiStatus, getErrorMessage } from "~base/Services/API";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import SelfServiceHeaderMenu from "../Shared/SelfServiceHeaderMenu";
import PageTermsOfService from "./InternalPages/PageTermsOfService";
import { getLocaleFromCookie } from "~base/Pages/Shared/LocalePicker";
import { useSettings } from "~base/Settings/SettingsContext";
import { useAuthToken } from "~base/Services/API/AuthToken";
import { useGetTextCodeMaps } from "~base/Services/API/Resource/GetTextCodeMaps";
import {
    loadedIds,
    loadTextCodeMapCallback,
} from "~base/Services/TextCodeService";
import type {
    PostSelfServiceLoginRegistrationRequest,
    PostSelfServiceLoginRegistrationResponseSuccess,
} from "~jrad/Services/API/Account/PostSelfServiceLoginRegistration";

import { usePostSelfServiceLoginRegistration } from "~jrad/Services/API/Account/PostSelfServiceLoginRegistration";

import {
    type SelfServiceLoginInfoTenant,
    useGetSelfServiceLoginInfo,
} from "~jrad/Services/API/Account/GetSelfServiceLoginInfo";
import PageAccessDenied from "./InternalPages/PageAccessDenied";
import { UserServiceProvider } from "~base/Services/ServerContext";
import { DomainLogoThemeProvider } from "~jrad/Styling/SoPNLConsilioThemeCustomerDomainLogo";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type SelfServiceLoginPageComponentProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const SelfServiceLoginPageComponent: React.FunctionComponent<
    SelfServiceLoginPageComponentProps
> = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const navigate = useNavigate();
    const settings = useSettings();
    const serverRelativePath =
        settings.valueAsString("serverVirtualPath") ?? "/";
    const [searchParams] = useSearchParams();

    const [state, dispatch] = useConsilioReducer(
        buildReducerId("SelfServicePage"),
        (
            state: SelfServiceLoginViewState,
            action: SelfServiceLoginViewAction
        ) => reducer(state, action, intl),
        initialState
    );

    const [_, currentAuthToken] = useAuthToken({});

    React.useEffect(() => {
        if (state.hasStoredAuthToken) {
            navigate("/");
        }
    }, [navigate, state.hasStoredAuthToken]);

    React.useEffect(() => {
        if (currentAuthToken?.access_token && !state.hasStoredAuthToken) {
            navigate("/Account/Logout");
        }
    }, [currentAuthToken, navigate, state.hasStoredAuthToken]);

    const locale =
        getLocaleFromCookie() ??
        settings.valueAsString("account.defaultLanguage");

    const currentRequestData: PostSelfServiceLoginRegistrationRequest = {
        Locale: locale,
        DomainAccessCode: searchParams.get("AccessCode"),
        DomainAccessKey: searchParams.get("AccessKey"),
        HasAcceptedToS: state.requestData.hasAcceptedToS,
    };

    const [loadingTextCodes, getTextCodesApi] = useGetTextCodeMaps({
        callback: async (status, response, responseData) => {
            loadTextCodeMapCallback(
                serverRelativePath,
                status,
                dispatch,
                response,
                responseData
            );
        },
    });

    const [loadingLoginInfo] = useGetSelfServiceLoginInfo({
        locale: locale,
        domainAccessCode: searchParams.get("AccessCode"),
        domainAccessKey: searchParams.get("AccessKey"),
        call:
            !loadingTextCodes &&
            !state.loginInfo &&
            !state.errorMessage &&
            !state.waitForTextCodesToLoad,
        callback: async (
            status,
            response,
            responseData,
            requestData,
            context,
            traceContext
        ) => {
            if (response.status == 404) {
                dispatch({
                    type: "setCurrentPage",
                    page: SelfServiceLoginRegistrationPage.AccessDenied,
                });
            } else {
                await callbackWithErrorHandling(
                    intl,
                    dispatch,
                    status,
                    response,
                    async () => {
                        const tenant = responseData.Tenant;

                        if (tenant) {
                            const ids = loadedIds();

                            dispatch({ type: "waitForTextCodesToLoad" });

                            await getTextCodesApi({
                                keys: ids,
                                locale: locale,
                                tenant: tenant.TenantName,
                            });
                        }

                        dispatch({
                            type: "setLoginInfo",
                            data: responseData,
                        });
                    }
                );
            }
        },
    });

    const [processingRegistrationRequest, postRegistrationRequest] =
        usePostSelfServiceLoginRegistration({
            data: currentRequestData,
            callback: async (
                status,
                response,
                responseData,
                requestData,
                context,
                traceContext
            ) => {
                await callbackWithErrorHandling(
                    intl,
                    dispatch,
                    status,
                    response,
                    async () => {
                        const successResponseData =
                            responseData as PostSelfServiceLoginRegistrationResponseSuccess;

                        dispatch({
                            type: "setResponseData",
                            data: responseData,
                        });
                    }
                );
            },
        });

    const triggerRegistrationRequest = () => {
        postRegistrationRequest(currentRequestData);
    };

    const isLoading =
        loadingLoginInfo ||
        (!state.loginInfo &&
            state.page != SelfServiceLoginRegistrationPage.AccessDenied) ||
        loadingTextCodes ||
        processingRegistrationRequest ||
        state.hasStoredAuthToken ||
        state.waitForTextCodesToLoad;

    React.useEffect(() => {
        if (!isLoading && state.page != state.nextPageAfterLoading) {
            dispatch({
                type: "setCurrentPage",
                page: state.nextPageAfterLoading,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, state.page, state.nextPageAfterLoading]);

    const tenant = state.loginInfo?.Tenant;

    return (
        <UserServiceProvider
            sessionMetadata={{
                locale: "",
                subjectDisplayName: "",
                subjectIdentifier: "",
                sessionModelFqn: "",
                metadata: state.loginInfo?.DomainUid
                    ? [{ Key: "DomainUid", Value: state.loginInfo.DomainUid }]
                    : [],
            }}
            getServiceContext={(_user, sessionMetadata) => ({
                sessionMetadata,
            })}
        >
            <DomainLogoThemeProvider>
                <ApplicationPage
                    isLoading={isLoading}
                    headerMenu={<SelfServiceHeaderMenu />}
                    centerVertical={false}
                    handleMargins={false}
                    contentWrapperClassName={classes.pageContainer}
                >
                    <IntroDescription
                        state={state}
                        dispatch={dispatch}
                        tenant={tenant}
                    />
                    {state.errorMessage && (
                        <p className={classes.errorMessage}>
                            {state.errorMessage}
                        </p>
                    )}
                    <InternalPage
                        isLoading={isLoading}
                        state={state}
                        dispatch={dispatch}
                        postRegistrationRequest={triggerRegistrationRequest}
                        currentRequestData={currentRequestData}
                        tenant={tenant}
                    />
                </ApplicationPage>
            </DomainLogoThemeProvider>
        </UserServiceProvider>
    );
};

//-------------------------------------------------------------------------------------
// Private functions
//-------------------------------------------------------------------------------------

interface SelfServiceLoginInternalPageProps {
    isLoading: boolean;
    state: SelfServiceLoginViewState;
    dispatch: React.Dispatch<SelfServiceLoginViewAction>;
    postRegistrationRequest: () => void;
    currentRequestData: PostSelfServiceLoginRegistrationRequest;
    tenant: SelfServiceLoginInfoTenant;
}

const InternalPage: React.FunctionComponent<
    SelfServiceLoginInternalPageProps
> = (props) => {
    switch (props.state.page) {
        case SelfServiceLoginRegistrationPage.AccessDenied: {
            return <PageAccessDenied {...props} />;
        }
        case SelfServiceLoginRegistrationPage.TermsOfService: {
            return <PageTermsOfService {...props} />;
        }
    }
};

interface SelfServiceLoginIntroDescriptionProps {
    state: SelfServiceLoginViewState;
    dispatch: React.Dispatch<SelfServiceLoginViewAction>;
    tenant: SelfServiceLoginInfoTenant;
}

const IntroDescription: React.FunctionComponent<
    SelfServiceLoginIntroDescriptionProps
> = ({ state, dispatch, tenant }) => {
    const intl = useIntl();
    const classes = useStyles();
    const settings = useSettings();

    const registrationTenant = tenant?.TenantName;
    const tenantName = settings.valueAsString("detectedTenantName");
    const tenantDiplayName =
        intl.tryFormatText("VisiFinance.UI.TenantDisplayName") ??
        registrationTenant ??
        tenantName;

    const customerName = state.loginInfo?.CustomerName;

    switch (state.page) {
        case SelfServiceLoginRegistrationPage.AccessDenied:
            return null;
        default:
            break;
    }

    const introDescFQN = "SoPNL.UI.SelfServiceLogin.About.Description";
    const introHeaderFQN = "SoPNL.UI.SelfServiceLogin.About.Header";

    return (
        <>
            <h1 className={classes.introHeader}>
                {intl.formatText(introHeaderFQN, tenantDiplayName)}
            </h1>
            <p className={classes.introDesc}>
                {intl.formatText(introDescFQN, tenantDiplayName, customerName)}
            </p>
            <hr className={classes.introDivider} />
        </>
    );
};

const callbackWithErrorHandling = async (
    intl: IntlContext,
    dispatch: React.Dispatch<SelfServiceLoginViewAction>,
    status: ApiStatus,
    response: Response,
    successAction: () => Promise<void>
) => {
    if (status == "Success") {
        await successAction();
    } else {
        dispatch({
            type: "fetchError",
            message: await getErrorMessage(intl, response),
        });
    }
};

export default SelfServiceLoginPageComponent;
